import './Footer.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import React from 'react';

import { Accordion } from './Accordion';
import { useAppStaticContext } from './AppContext';
import { Container } from './Container';
import { FooterContentType } from './contentTypes';
import { NewsletterForm } from './forms/NewsletterForm';
import { t } from './i18n/i18n';
import logoFooter from './images/logo-footer.svg';
import { ListAFromMenu } from './ListAFromMenu';
import { SocialIcons } from './SocialIcons';

export interface FooterProps {}

export const Footer = (props: FooterProps) => {
  const footerMenu = b2x.useMenu('MENU_FOOTER');
  const footerBottomMenu = b2x.useMenu('MENU_BOTTOM_FOOTER');
  const [currentBreakpoint] = React.useState<string>(b2x.getCurrentBreakpoint());

  const { setFooterCopyrightRef } = useAppStaticContext();

  const footerContent = b2x.useContent<FooterContentType>('FOOTER_CONTENT');
  return (
    <footer className="footer mt-auto border-top">
      <div className="bg-primary">
        <Container>
          <div className="firstRow py-4">
            <b2x.Row className="justify-content-center my-1 my-lg-3" gap={2}>
              <b2x.Col size={{ lg: 3, xs: 12 }}>
                <div className="d-flex flex-column justify-content-between h-100">
                  <div className="logoFooter pb-4 py-lg-0">
                    <b2x.Image fluid src={logoFooter} />
                  </div>
                  <div className="d-none d-lg-flex align-items-center text-white">
                    <div className="pe-3 text-uppercase fw-bold">{t('misc.followUs')}</div>
                    <SocialIcons />
                  </div>
                </div>
              </b2x.Col>
              <b2x.Col className="order-3" size={{ lg: 5, xs: 12 }}>
                <AccordionFooterMenu id="first-footer-accordion" menu={footerMenu} />
              </b2x.Col>
              <b2x.Col className="order-2 order-lg-3" size={{ lg: 4, xs: 12 }}>
                <div className="newsletter text-white px-1 px-lg-0">
                  <h6
                    className={classnames('fw-bold h2 text-uppercase d-none d-lg-block', {
                      h4: 'xs sm md'.includes(currentBreakpoint),
                    })}
                  >
                    {t('misc.newsletterTitle')}
                  </h6>
                  <p className="fw-light">{b2x.formatHtml(footerContent?.body.newsletterSubtitle)}</p>
                  <NewsletterForm emailFieldStyle="inputGroup" source="website-footer" />
                  <div className="d-flex d-lg-none justify-content-center align-items-center text-white py-3">
                    <SocialIcons size={48} />
                  </div>
                </div>
              </b2x.Col>
            </b2x.Row>
          </div>
        </Container>
      </div>
      <Container>
        <div className="bottom-footer align-items-center py-2 fw-light extra-small" ref={setFooterCopyrightRef}>
          <b2x.Row>
            <b2x.Col size={{ lg: 3, xs: 12 }}>
              {footerContent && (
                <div className="bottom-copyright py-lg-0 py-2">{b2x.formatHtml(footerContent.body.copyright)}</div>
              )}
            </b2x.Col>
            <b2x.Col size={{ lg: 6, xs: 12 }} style={{ height: 'fit-content', marginBlock: 'auto' }}>
              <div className="bottom-list d-flex justify-content-center pb-lg-0 pb-3 text-uppercase">
                {footerBottomMenu && <ListAFromMenu menu={footerBottomMenu} textUppercase />}
              </div>
            </b2x.Col>
            <b2x.Col size={{ lg: 3, xs: 12 }} style={{ height: 'fit-content', marginBlock: 'auto' }}>
              <div className="bottom-payments pb-lg-0 pb-3">
                <b2x.PaymentIcons justifyContent="end" />
              </div>
            </b2x.Col>
          </b2x.Row>
        </div>
      </Container>
    </footer>
  );
};

interface AccordionFooterMenuProps {
  id: string;
  menu?: b2x.MenuApiDto;
}
const AccordionFooterMenu = ({ id, menu }: AccordionFooterMenuProps) => {
  const { session } = b2x.useAppContext();
  const { showAccountOffcanvas } = useAppStaticContext();
  return (
    <Accordion
      columnLayoutFrom="lg"
      id={id}
      itemBodyClassName="small pb-3"
      itemButtonClassName="bg-transparent text-white text-uppercase fw-bold mb-lg-1 p-3 p-lg-0"
      itemClassName="bg-transparent"
      itemHeaderClassName="text-uppercase"
    >
      {menu?.children.map(
        (column) =>
          column.label && (
            <b2x.AccordionItem id={`accordionItem-${column.id}`} key={column.code} title={column.label}>
              {column.children.map((li) =>
                li.code === 'SITE_CCN_AREA' ? (
                  session?.userLogged ? (
                    li.children.map(
                      (subLi) =>
                        subLi.link && (
                          <div key={subLi.code}>
                            <b2x.router.Link
                              className="d-block py-2 text-decoration-none text-white fw-light"
                              to={subLi.link}
                            >
                              {subLi.label}
                            </b2x.router.Link>
                          </div>
                        )
                    )
                  ) : (
                    <div key={li.code}>
                      <b2x.BlankButton className="d-block py-2 text-white fw-light" onClick={showAccountOffcanvas}>
                        {t('account.login')}
                      </b2x.BlankButton>
                    </div>
                  )
                ) : (
                  li.link && (
                    <div key={li.code}>
                      {li.image ? (
                        <b2x.router.Link className={'mt-2 d-inline-block'} to={li.link}>
                          <b2x.Image {...li.image} fluid width={100} />
                        </b2x.router.Link>
                      ) : (
                        <b2x.router.Link className="text-decoration-none text-white fw-light d-block py-2" to={li.link}>
                          {li.label}
                        </b2x.router.Link>
                      )}
                    </div>
                  )
                )
              )}
            </b2x.AccordionItem>
          )
      )}
    </Accordion>
  );
};
